import * as React from 'react';
import { ReactNode } from "react";
import * as api from 'shared/services/reportingApi';
import { evalPath } from 'helpers/HelperFunctions';
import { PageList } from 'components/ui/PageList';
import { LabelledCheckbox } from 'components/ui/LabelledCheckbox';
import { Grid2, GridSize } from "@mui/material";
import { RemoveOutlined, DragIndicatorOutlined } from '@mui/icons-material';


export interface IGridSizes {
    xs?: GridSize | undefined,
    sm?: GridSize | undefined,
    md?: GridSize | undefined,
    lg?: GridSize | undefined,
    xl?: GridSize | undefined
}

export const standardGridSizes: IGridSizes = { xs: 6, sm: 5, md: 4, lg: 3, xl: 2 };

export interface ICheckboxListProps<T> extends IGridSizes {
    items: T[],
    toggle: (item: T) => void,
    buildKey: (item: T) => string,
    isSelected: (item: T) => boolean,
    content: (item: T) => ReactNode
}

export interface IExportColumnListProps extends IGridSizes {
    columns: api.ExportColumn[],
    toggle: (col: api.ExportColumn) => void,
    selected: ISelectedExportColumn[]
}

export const ExportColumnList = (props: IExportColumnListProps) => {
    return (
        <Grid2 container className="column-list">
            {
                props.columns.map(
                    c => <Grid2 size={{ xs: props.xs, sm: props.sm, md: props.md, lg: props.lg, xl: props.xl }} key={c.name}>
                        <LabelledCheckbox
                            checkedValue={props.selected.findIndex(s => s.definition.name === c.name) > -1}
                            toggle={() => { props.toggle(c); }}>{c.title}</LabelledCheckbox>
                    </Grid2>
                )
            }
        </Grid2>
    );
};

export interface IExportGridProps {
    columns: ISelectedExportColumn[],
    results: api.IResultList,
    sortOrder: api.ISortConfig[],
    pageNumber: number,
    state: 'ready' | 'busy',
    moveToPage: (page: number) => void,
    pageSize: number,
    setPageSize: (value: number) => void,
    setSortOrder?: (value: api.ISortConfig[]) => void,
    removeColumn?: (col: api.ExportColumn) => void
}

export interface IExportColumnHeaderProps {
    col: ISelectedExportColumn;
    direction: string | null;
    setSortDirection: (dir: null | 'asc' | 'desc') => void;
    removeColumn?: () => void;
}

export const ExportColumnHeader = (props: IExportColumnHeaderProps) => {
    const toggleSortOrder = () => {
        if (!props.col.definition.unsortable) {
            switch (props.direction) {
                case "asc":
                    props.setSortDirection("desc");
                    break;
                case "desc":
                    props.setSortDirection(null);
                    break;
                default:
                    props.setSortDirection("asc");
                    break;
            }
        }
    };

    return (
        <td className="header-col" onClick={toggleSortOrder}>
            <div>

                <span>
                    <DragIndicatorOutlined className="drag-indicator" />
                    <span>{props.col.definition.title}</span>
                </span>
                <div>
                    {props.removeColumn != undefined &&
                        <RemoveOutlined className="remove-field" onClick={props.removeColumn} titleAccess="remove field" />
                    }
                </div>
            </div>
        </td>
    )
};

export const ExportGrid = (props: IExportGridProps) => {
    const getSortDirection = (name: string): string | null => {
        var sortField = props.sortOrder.find(s => s.field === name);

        if (sortField)
            return sortField.dir;

        return null;
    };

    const setSortDirection = (name: string, dir: null | 'asc' | 'desc') => {
        const arr = props.sortOrder;

        var idx = arr.findIndex(s => s.field === name);

        if (idx > -1) {
            if (dir === null) {
                arr.splice(idx, 1);
            } else if (dir !== undefined) {
                arr[idx].dir = dir;
            }
        } else {
            if (dir) {
                arr.push({ field: name, dir: dir });
            }
        }

        props.setSortOrder !== undefined && props.setSortOrder(arr);
    };

    const getFieldTitle = (name: string): string => {
        const field = props.columns.find(c => c.definition.name === name);

        if (!field) {
            return name;
        }

        return field.definition.title;
    }

    const sortDescription = () => {
        if (props.sortOrder.length === 0) {
            return "click a column header to stort";
        }

        return "sorted by: " + props.sortOrder.map(s => { return getFieldTitle(s.field) + (s.dir === 'asc' ? "" : " (" + s.dir + ")") }).join(', then by: ');
    };

    const getRemoveColumn = (xc: ISelectedExportColumn) => {
        if (props.removeColumn == undefined) {
            return undefined;
        }

        return () => { props.removeColumn != undefined && props.removeColumn(xc.definition); }
    }

    return (
        <>
            <div className="export-grid-container">
                <div className="export-grid-scroll">
                    <table className={"export-grid export-state-" + props.state}>
                        <tbody>
                            <tr title={sortDescription()}>
                                {
                                    props.columns.length == 0 &&
                                    <td>No columns selected</td>
                                }

                                {
                                    props.columns.map(xc =>
                                        <ExportColumnHeader
                                            key={xc.definition.name}
                                            col={xc}
                                            removeColumn={getRemoveColumn(xc)}
                                            setSortDirection={dir => { setSortDirection(xc.definition.name, dir); }}
                                            direction={getSortDirection(xc.definition.name)}
                                        />)
                                }
                            </tr>
                            {
                                props.results.list.map(
                                    (row, index) => <tr key={row.id} className={"export-grid-row " + ((index % 2 == 0) ? "odd" : "even")}>
                                        {
                                            props.columns.map(xc =>
                                                <ExportGridColumn
                                                    key={xc.definition.name}
                                                    row={row}
                                                    col={xc}
                                                />
                                            )
                                        }
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            <table className="export-grid-pages-list">
                <tbody>
                    <tr><td className="page-list" colSpan={props.columns.length}>
                        <PageList
                            totalResults={props.results.totalResults}
                            pageSize={props.pageSize}
                            pageNumber={props.pageNumber}
                            moveToPage={props.moveToPage}
                        />
                    </td></tr>
                </tbody>
            </table>
        </>
    );
};

export interface IExportGridColumnProps {
    row: any,
    col: ISelectedExportColumn
}

export const ExportGridColumn = (props: IExportGridColumnProps) => {
    return (
        <td className={"grid-col" + (props.col.definition.infoDialog ? " info-dialog-link" : "")} onClick={() => { if (props.col.definition.infoDialog) props.col.definition.infoDialog(props.row); }}>
            {props.col.definition.formatter ? props.col.definition.formatter(evalPath(props.col.definition.name, props.row)) : evalPath(props.col.definition.name, props.row)}
        </td>
    )
};

export interface ISelectedExportColumn {
    seq: number;
    definition: api.ExportColumn;
}
